import type { ComponentMismatch } from '../types/trade.types';

/**
 * Agrupa los descuadres del tarifario por naviera, agente y monto faltante.
 *
 * **Por qué agrupar.** El Excel de Delicia produce 88 filas marítimas cuyo
 * `Total Costo Marítimo` no coincide con la suma de sus conceptos. Listadas una
 * por una no son accionables: nadie revisa 88 renglones, y quien las mira concluye
 * "está todo roto".
 *
 * Agrupadas son **4 patrones**, y el hueco es **fijo dentro de cada uno**:
 *
 * ```
 * ONE   · MSL BOLIVIA · faltan 300   36 filas
 * MSC   · UNION CARGO · faltan  40   24 filas   (solo las de Iquique)
 * COSCO · MSL BOLIVIA · faltan 238   14 filas
 * COSCO · MSL BOLIVIA · faltan 248   14 filas
 * ```
 *
 * Eso ya es una pregunta concreta que se puede llevar a quien arma el tarifario, y
 * que se arregla en la fuente en vez de parchearse acá.
 *
 * El monto entra en la clave a propósito: COSCO tiene **dos** huecos distintos con
 * el mismo agente, y promediarlos escondería que son dos casos separados —
 * probablemente dos períodos o dos acuerdos.
 */
export interface MismatchGroup {
  key: string;
  carrier: string;
  agent: string | null;
  /** El monto que falta en cada fila del grupo. Es idéntico para todas. */
  difference: number;
  rows: number;
}

export function groupMismatches(mismatches: ComponentMismatch[]): MismatchGroup[] {
  const groups = new Map<string, MismatchGroup>();

  for (const m of mismatches) {
    const key = `${m.carrier}|${m.agent ?? ''}|${m.difference}`;
    const existing = groups.get(key);
    if (existing) {
      existing.rows++;
      continue;
    }
    groups.set(key, {
      key,
      carrier: m.carrier,
      agent: m.agent,
      difference: m.difference,
      rows: 1,
    });
  }

  /*
    El grupo más grande primero: es el que más rinde arreglar. A igual tamaño, el
    hueco más grande — es el que más plata mueve.
  */
  return [...groups.values()].sort((a, b) => b.rows - a.rows || b.difference - a.difference);
}
